import { useState, useEffect, useRef, useCallback } from 'react';
import { styled } from '@mui/material/styles';

// Custom hook for speech recognition functionality
const useSpeechRecognition = () => {
    const [transcript, setTranscript] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [isSpeechRecognitionSupported, setIsSpeechRecognitionSupported] = useState(false);
    const recognitionRef = useRef(null);

    useEffect(() => {
        // Check if speech recognition is supported
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (SpeechRecognition) {
            setIsSpeechRecognitionSupported(true);
            recognitionRef.current = new SpeechRecognition();

            const recognition = recognitionRef.current;
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                let interimTranscript = '';
                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    } else {
                        interimTranscript += event.results[i][0].transcript;
                    }
                }
                setTranscript(finalTranscript || interimTranscript);
            };

            recognition.onstart = () => {
                setIsListening(true);
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognition.onerror = (event) => {
                setIsListening(false);

                // Handle specific error types
                switch (event.error) {
                    case 'not-allowed':
                        setIsSpeechRecognitionSupported(false);
                        console.warn('Microphone access was denied');
                        break;
                    case 'no-speech':
                        console.warn('No speech was detected');
                        break;
                    case 'audio-capture':
                        setIsSpeechRecognitionSupported(false);
                        console.warn('No microphone was found');
                        break;
                    case 'network':
                        console.warn('Network error occurred');
                        break;
                    default:
                        console.warn('Speech recognition error occurred');
                }
            };
        } else {
            console.warn('Speech recognition not supported in this browser');
        }

        // Cleanup function
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const startListening = useCallback(async () => {
        if (!isSpeechRecognitionSupported || !recognitionRef.current) {
            console.warn('Speech recognition is not supported');
            return;
        }

        try {
            if (!isListening) {
                setTranscript(''); // Clear previous transcript
                recognitionRef.current.start();
            }
        } catch (error) {
            // Handle permission errors
            if (error.name === 'NotAllowedError') {
                console.warn('Microphone access was denied');
            }
            else if (error.name === 'NotFoundError') {
                console.warn('No microphone was found');
            }
            else {
                console.warn('Unable to access microphone');
            }
        }
    }, [isSpeechRecognitionSupported, isListening]);

    const stopListening = useCallback(() => {
        if (recognitionRef.current && isListening) {
            recognitionRef.current.stop();
        }
    }, [isListening]);

    return {
        transcript,
        isListening,
        isSpeechRecognitionSupported,
        startListening,
        stopListening,
        setTranscript
    };
};

const ListeningIndicator = () => {
    const StyledListeningIndicator = styled('div')({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        marginRight: '10px',
        '& .listening-text': {
            fontWeight: '500'
        },
        '& .soundwave': {
            display: 'flex',
            gap: '2px',
            alignItems: 'center',
            '& .wave': {
                width: '3px',
                borderRadius: '20px',
                animation: 'soundwave 1.2s ease-in-out infinite',
                '&:nth-of-type(1)': {
                    height: '15px',
                    animationDelay: '0s',
                    backgroundColor: '#999' // Light gray
                },
                '&:nth-of-type(2)': {
                    height: '19px',
                    animationDelay: '0.1s',
                    backgroundColor: '#666' // Medium gray
                },
                '&:nth-of-type(3)': {
                    height: '13px',
                    animationDelay: '0.2s',
                    backgroundColor: '#444' // Dark gray
                },
                '&:nth-of-type(4)': {
                    height: '17px',
                    animationDelay: '0.3s',
                    backgroundColor: '#999' // Light gray (repeating pattern)
                },
                '&:nth-of-type(5)': {
                    height: '15px',
                    animationDelay: '0.4s',
                    backgroundColor: '#666' // Medium gray (repeating pattern)
                }
            }
        },
        '@keyframes soundwave': {
            '0%, 40%, 100%': {
                transform: 'scaleY(0.4)'
            },
            '20%': {
                transform: 'scaleY(1.0)'
            }
        }
    });
    return (
        <StyledListeningIndicator className="listening-indicator">
            <span className="listening-text">Listening</span>
            <div className="soundwave">
                <div className="wave"></div>
                <div className="wave"></div>
                <div className="wave"></div>
                <div className="wave"></div>
                <div className="wave"></div>
            </div>
        </StyledListeningIndicator >
    );
};

export {
    useSpeechRecognition, 
    ListeningIndicator,
};